import { useEffect, useState } from 'react';
import api from '../../../utils/axiosConfig';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Loader2 } from 'lucide-react';
import {
  PERIOD_BUCKETS,
  normalizePeriodBuckets,
} from '../../../utils/periodizationPeriodBuckets';

function attrLabel(t, typeKey, fallback) {
  const k = `periodization.attributes.${typeKey}`;
  const tr = t(k);
  return tr !== k ? tr : fallback || typeKey;
}

export default function AdminAssessmentSetsSection({ sets, types, onReload, saving, setSaving }) {
  const { t } = useLanguage();
  const [editingSetId, setEditingSetId] = useState(null);
  const [editSetName, setEditSetName] = useState('');
  const [editSetDescription, setEditSetDescription] = useState('');
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  const [bucketMatrix, setBucketMatrix] = useState({});

  const editingSet = sets.find((s) => s.id === editingSetId);

  useEffect(() => {
    if (!editingSetId) return;
    const set = sets.find((s) => s.id === editingSetId);
    if (set) openSetEditor(set);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only resync when server data refreshes
  }, [sets]);

  const openSetEditor = (set) => {
    setEditingSetId(set.id);
    setEditSetName(set.name || '');
    setEditSetDescription(set.description || '');
    const ids = (set.attribute_types || []).map((at) => at.id);
    setSelectedTypeIds(ids);
    const next = {};
    for (const at of set.attribute_types || []) {
      next[at.id] = normalizePeriodBuckets(at.period_buckets, at.period_buckets_allowed);
    }
    setBucketMatrix(next);
  };

  const closeEditor = () => {
    setEditingSetId(null);
    setEditSetName('');
    setEditSetDescription('');
    setSelectedTypeIds([]);
    setBucketMatrix({});
  };

  const toggleTypeInSet = (typeId) => {
    setSelectedTypeIds((prev) => {
      if (prev.includes(typeId)) {
        const next = prev.filter((id) => id !== typeId);
        setBucketMatrix((bm) => {
          const copy = { ...bm };
          delete copy[typeId];
          return copy;
        });
        return next;
      }
      const type = types.find((x) => x.id === typeId);
      setBucketMatrix((bm) => ({
        ...bm,
        [typeId]: normalizePeriodBuckets(type?.period_buckets_allowed || type?.period_buckets),
      }));
      return [...prev, typeId];
    });
  };

  const toggleBucketForType = (typeId, bucket, allowed) => {
    if (!allowed[bucket]) return;
    setBucketMatrix((prev) => {
      const enabled = normalizePeriodBuckets(prev[typeId], allowed);
      return {
        ...prev,
        [typeId]: { ...enabled, [bucket]: !enabled[bucket] },
      };
    });
  };

  const buildAttributeItemsPayload = () =>
    selectedTypeIds.map((typeId) => ({
      attribute_type_id: typeId,
      period_buckets: bucketMatrix[typeId] || normalizePeriodBuckets(types.find((x) => x.id === typeId)?.period_buckets_allowed),
    }));

  const saveSet = async () => {
    if (!editingSetId) return;
    setSaving(true);
    try {
      await api.put(`/api/admin/periodization-catalog/assessment-sets/${editingSetId}`, {
        name: editSetName.trim() || undefined,
        description: editSetDescription.trim() || null,
        attribute_type_ids: selectedTypeIds,
        attribute_items: buildAttributeItemsPayload(),
      });
      await onReload?.();
    } catch (e) {
      window.alert(e.response?.data?.error || t('periodization.assessmentManager.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const setAsDefault = async (setId) => {
    setSaving(true);
    try {
      await api.put(`/api/admin/periodization-catalog/assessment-sets/${setId}`, { is_default: true });
      await onReload?.();
    } catch (e) {
      window.alert(e.response?.data?.error || t('periodization.assessmentManager.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle className='text-base'>{t('periodization.admin.builtinSetsTitle')}</CardTitle>
          <CardDescription>{t('periodization.admin.setsEditHint')}</CardDescription>
        </CardHeader>
        <CardContent className='space-y-3'>
          {sets.map((set) => (
            <div
              key={set.id}
              className={`rounded-lg border p-4 space-y-2 ${
                editingSetId === set.id ? 'border-primary bg-primary/5' : 'border-border'
              }`}
            >
              <div className='flex flex-wrap items-start justify-between gap-2'>
                <div>
                  <p className='font-medium text-foreground'>
                    {set.name}{' '}
                    <span className='text-muted-foreground font-normal font-mono text-xs'>({set.set_key})</span>
                  </p>
                  {set.description ? (
                    <p className='text-xs text-muted-foreground mt-0.5'>{set.description}</p>
                  ) : null}
                </div>
                <div className='flex flex-wrap items-center gap-2'>
                  {set.is_default ? <Badge>{t('periodization.admin.default')}</Badge> : null}
                  <Badge variant='outline'>
                    {t('periodization.admin.colRank')} {set.complexity_rank}
                  </Badge>
                  <Badge variant='secondary'>
                    {t('periodization.admin.itemsCount', { count: (set.attribute_types || []).length })}
                  </Badge>
                </div>
              </div>
              <p className='text-xs text-muted-foreground leading-relaxed'>
                {(set.attribute_types || [])
                  .map((at) => attrLabel(t, at.type_key, at.name))
                  .join(' · ')}
              </p>
              <div className='flex flex-wrap gap-2'>
                <Button type='button' size='sm' variant={editingSetId === set.id ? 'default' : 'outline'} onClick={() => openSetEditor(set)}>
                  {editingSetId === set.id ? t('periodization.admin.editingSet') : t('common.edit')}
                </Button>
                {!set.is_default && (
                  <Button type='button' size='sm' variant='outline' onClick={() => setAsDefault(set.id)} disabled={saving}>
                    {t('periodization.admin.setAsDefault')}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {editingSet && (
        <Card className='border-primary/40'>
          <CardHeader>
            <CardTitle className='text-base'>{t('periodization.assessmentManager.editSet')}</CardTitle>
            <CardDescription>{t('periodization.assessmentManager.editAttributesHint')}</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='grid gap-3 sm:grid-cols-2'>
              <div>
                <Label className='text-xs'>{t('periodization.assessmentManager.editSet')}</Label>
                <Input className='mt-1' value={editSetName} onChange={(e) => setEditSetName(e.target.value)} />
              </div>
              <div>
                <Label className='text-xs'>{t('periodization.admin.schemaDescription')}</Label>
                <Input
                  className='mt-1'
                  value={editSetDescription}
                  onChange={(e) => setEditSetDescription(e.target.value)}
                />
              </div>
            </div>

            <div className='rounded-md border border-border divide-y divide-border max-h-64 overflow-y-auto'>
              {types.map((type) => (
                <div key={type.id} className='flex items-center justify-between gap-3 px-3 py-2 text-sm'>
                  <span>{attrLabel(t, type.type_key, type.name)}</span>
                  <Switch
                    checked={selectedTypeIds.includes(type.id)}
                    onCheckedChange={() => toggleTypeInSet(type.id)}
                  />
                </div>
              ))}
            </div>

            {selectedTypeIds.length > 0 && (
              <div className='space-y-2'>
                <p className='text-xs font-medium'>{t('periodization.assessmentManager.bucketMatrixTitle')}</p>
                <p className='text-[10px] text-muted-foreground'>
                  {t('periodization.assessmentManager.bucketMatrixHint')}
                </p>
                <div className='overflow-x-auto rounded-md border border-border'>
                  <table className='w-full min-w-[420px] text-xs'>
                    <thead>
                      <tr className='border-b border-border bg-muted/40'>
                        <th className='p-2 text-left font-medium'>{t('periodization.attribute')}</th>
                        {PERIOD_BUCKETS.map((bucket) => (
                          <th key={bucket} className='p-2 text-center font-medium'>
                            {t(`periodization.assessmentManager.buckets.${bucket}`)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTypeIds.map((typeId) => {
                        const type = types.find((x) => x.id === typeId);
                        if (!type) return null;
                        const allowed = normalizePeriodBuckets(type.period_buckets_allowed);
                        const enabled = normalizePeriodBuckets(bucketMatrix[typeId], allowed);
                        return (
                          <tr key={typeId} className='border-b border-border/60'>
                            <td className='p-2 font-medium'>{attrLabel(t, type.type_key, type.name)}</td>
                            {PERIOD_BUCKETS.map((bucket) => (
                              <td key={bucket} className='p-2 text-center'>
                                <input
                                  type='checkbox'
                                  className='h-4 w-4 accent-primary'
                                  checked={!!enabled[bucket]}
                                  disabled={!allowed[bucket]}
                                  onChange={() => toggleBucketForType(typeId, bucket, allowed)}
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className='flex gap-2 justify-end'>
              <Button type='button' variant='ghost' onClick={closeEditor}>
                {t('common.cancel')}
              </Button>
              <Button type='button' onClick={saveSet} disabled={saving}>
                {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : t('common.save')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
